// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Eye, EyeOff, Pin, PinOff, Shield } from 'lucide-react';
import { CoinHeaderCell, StandardHeaderCell, getTableHeaderCellClasses, getStickyPositionStyle } from './TableHeaderCells';
import { ColumnTooltip } from './ColumnTooltip';
import CoinLogoImage from './CoinLogoImage';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

interface HedgePerformer {
  rank: number;
  coin_id: string;
  symbol: string;
  name: string;
  logo_url?: string;
  avg_return_btc_down: number;
  median_return_btc_down: number;
  hit_rate: number;
  correlation_with_btc: number;
  volatility: number;
  hedge_score: number;
  btc_down_days_count: number;
  positive_days_count: number;
  current_price: number;
  volume_24h: number;
  data_date: string;
}

interface HedgePerformersData {
  summary: {
    total_analyzed: number;
    positive_performers: number;
    avg_hedge_score: number;
    timeframe: string;
    insight: string;
  };
  performers: HedgePerformer[];
  timestamp: string;
}

interface HedgePerformersProps {
  isOpen?: boolean;
  onToggle?: () => void;
}

const HedgePerformers: React.FC<HedgePerformersProps> = ({ isOpen: isOpenProp, onToggle: onToggleProp }) => {
  const { isDark } = useTheme();
  const [internalIsOpen, setInternalIsOpen] = useState(true);
  const [data, setData] = useState<HedgePerformersData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTimeframe, setActiveTimeframe] = useState<'1Y' | '1M'>('1Y');
  const tableScrollRef = useRef<HTMLDivElement>(null);

  // Use internal state if no external control is provided
  const isOpen = isOpenProp !== undefined ? isOpenProp : internalIsOpen;
  const handleToggle = onToggleProp || (() => setInternalIsOpen(!internalIsOpen));

  // Mock data for fallback when API fails
  const mockData: HedgePerformersData = {
    summary: {
      total_analyzed: 100,
      positive_performers: 23,
      avg_hedge_score: 0.156,
      timeframe: "1Y",
      insight: "During BTC downturns, stablecoins and defensive assets like GOLD show strongest hedge characteristics, with 23% of analyzed assets providing positive returns when Bitcoin falls."
    },
    performers: [
      {
        rank: 1,
        coin_id: "tether",
        symbol: "USDT",
        name: "Tether",
        avg_return_btc_down: 0.12,
        median_return_btc_down: 0.08,
        hit_rate: 87.5,
        correlation_with_btc: -0.05,
        volatility: 0.89,
        hedge_score: 0.92,
        btc_down_days_count: 142,
        positive_days_count: 124,
        current_price: 1.001,
        volume_24h: 24780000000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 2,
        coin_id: "paxos-standard",
        symbol: "PAXG",
        name: "PAX Gold",
        avg_return_btc_down: 0.34,
        median_return_btc_down: 0.28,
        hit_rate: 72.8,
        correlation_with_btc: -0.12,
        volatility: 1.45,
        hedge_score: 0.85,
        btc_down_days_count: 142,
        positive_days_count: 103,
        current_price: 2687.45,
        volume_24h: 12890000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 3,
        coin_id: "usd-coin",
        symbol: "USDC",
        name: "USD Coin",
        avg_return_btc_down: 0.08,
        median_return_btc_down: 0.05,
        hit_rate: 84.2,
        correlation_with_btc: -0.02,
        volatility: 0.76,
        hedge_score: 0.81,
        btc_down_days_count: 142,
        positive_days_count: 120,
        current_price: 0.9998,
        volume_24h: 5670000000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 4,
        coin_id: "first-digital-usd",
        symbol: "FDUSD",
        name: "First Digital USD",
        avg_return_btc_down: 0.06,
        median_return_btc_down: 0.04,
        hit_rate: 81.7,
        correlation_with_btc: 0.01,
        volatility: 0.92,
        hedge_score: 0.78,
        btc_down_days_count: 142,
        positive_days_count: 116,
        current_price: 1.0002,
        volume_24h: 890000000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 5,
        coin_id: "binance-usd",
        symbol: "BUSD",
        name: "Binance USD",
        avg_return_btc_down: 0.05,
        median_return_btc_down: 0.03,
        hit_rate: 79.6,
        correlation_with_btc: 0.03,
        volatility: 1.12,
        hedge_score: 0.74,
        btc_down_days_count: 142,
        positive_days_count: 113,
        current_price: 0.9996,
        volume_24h: 423000000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 6,
        coin_id: "terra-luna-2",
        symbol: "LUNA",
        name: "Terra",
        avg_return_btc_down: 0.89,
        median_return_btc_down: 0.45,
        hit_rate: 45.8,
        correlation_with_btc: 0.23,
        volatility: 12.7,
        hedge_score: 0.31,
        btc_down_days_count: 142,
        positive_days_count: 65,
        current_price: 0.445,
        volume_24h: 89700000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 7,
        coin_id: "cosmos",
        symbol: "ATOM",
        name: "Cosmos",
        avg_return_btc_down: -0.23,
        median_return_btc_down: -0.18,
        hit_rate: 42.3,
        correlation_with_btc: 0.67,
        volatility: 8.9,
        hedge_score: 0.28,
        btc_down_days_count: 142,
        positive_days_count: 60,
        current_price: 4.67,
        volume_24h: 134000000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 8,
        coin_id: "dogecoin",
        symbol: "DOGE",
        name: "Dogecoin",
        avg_return_btc_down: -0.45,
        median_return_btc_down: -0.34,
        hit_rate: 38.7,
        correlation_with_btc: 0.78,
        volatility: 11.2,
        hedge_score: 0.22,
        btc_down_days_count: 142,
        positive_days_count: 55,
        current_price: 0.109,
        volume_24h: 1240000000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 9,
        coin_id: "cardano",
        symbol: "ADA",
        name: "Cardano",
        avg_return_btc_down: -0.67,
        median_return_btc_down: -0.52,
        hit_rate: 35.2,
        correlation_with_btc: 0.82,
        volatility: 9.8,
        hedge_score: 0.18,
        btc_down_days_count: 142,
        positive_days_count: 50,
        current_price: 0.356,
        volume_24h: 267000000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 10,
        coin_id: "polkadot",
        symbol: "DOT",
        name: "Polkadot",
        avg_return_btc_down: -0.89,
        median_return_btc_down: -0.67,
        hit_rate: 31.7,
        correlation_with_btc: 0.85,
        volatility: 10.4,
        hedge_score: 0.15,
        btc_down_days_count: 142,
        positive_days_count: 45,
        current_price: 4.18,
        volume_24h: 134000000,
        data_date: new Date().toISOString().split('T')[0]
      }
    ],
    timestamp: new Date().toISOString()
  };

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<{[key: string]: boolean}>(() => {
    const saved = localStorage.getItem('hedgePerformersBtcColumns');
    return saved ? JSON.parse(saved) : {
      rank: true,
      coin: true,
      hedgeScore: true,
      avgReturn: true,
      hitRate: true,
      correlation: true,
      volatility: true,
      downDays: true,
    };
  });

  // Pinned columns state
  const [pinnedColumns, setPinnedColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem('hedgePerformersBtcPinnedColumns');
    return saved ? JSON.parse(saved) : [];
  });

  // Toast notification for pin limit
  const [showPinLimitToast, setShowPinLimitToast] = useState(false);

  type RawPerformer = any;

  function normalizePayload(raw: any): HedgePerformersData | null {
    if (!raw) return null;

    // Allow both { performers, summary } and { data: { performers, summary } }
    const root = raw.data?.performers ? raw.data : raw;

    // Accept alternative key names and map them into our interface
    const performers: HedgePerformer[] = Array.isArray(root.performers)
      ? root.performers.map((p: RawPerformer, i: number) => ({
          rank: Number(p.rank ?? i + 1),
          coin_id: p.coin_id ?? p.id ?? '',
          symbol: p.symbol ?? p.ticker ?? '',
          name: p.name ?? p.full_name ?? p.symbol ?? '',
          logo_url: p.logo_url ?? '',
          avg_return_btc_down: Number(p.avg_return_btc_down ?? p.avg_return_on_btc_down ?? p.avg_return ?? 0),
          median_return_btc_down: Number(p.median_return_btc_down ?? p.median_return ?? 0),
          hit_rate: Number(p.hit_rate ?? p.win_rate ?? 0),
          correlation_with_btc: Number(p.correlation_with_btc ?? p.btc_corr ?? p.corr ?? 0),
          volatility: Number(p.volatility ?? p.stdev ?? 0),
          hedge_score: Number(p.hedge_score ?? p.score ?? 0),
          btc_down_days_count: Number(p.btc_down_days_count ?? p.down_days ?? 0),
          positive_days_count: Number(p.positive_days_count ?? p.pos_days ?? 0),
          current_price: Number(p.current_price ?? p.price ?? 0),
          volume_24h: Number(p.volume_24h ?? p.volume ?? 0),
          data_date: String(p.data_date ?? p.date ?? ''),
        }))
      : [];

    const summary = root.summary ?? {};
    const normalized: HedgePerformersData = {
      summary: {
        total_analyzed: Number(summary.total_analyzed ?? performers.length ?? 0),
        positive_performers: Number(summary.positive_performers ?? performers.filter(x => x.avg_return_btc_down > 0).length),
        avg_hedge_score: Number(summary.avg_hedge_score ?? (performers.length ? performers.reduce((s, p) => s + (p.hedge_score || 0), 0) / performers.length : 0)),
        timeframe: String(summary.timeframe ?? '1Y'),
        insight: String(summary.insight ?? 'No data available'),
      },
      performers,
      timestamp: String(root.timestamp ?? new Date().toISOString()),
    };

    // If no performers, treat as invalid to preserve existing (mock) data
    if (!normalized.performers || normalized.performers.length === 0) return null;
    return normalized;
  }

  const fetchHedgePerformers = async (timeframe: string) => {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (!token) {
      setError('Authentication required. Please log in to view hedge performers data.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/hedge-performers?timeframe=${timeframe}&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const raw = await response.json();
      const normalized = normalizePayload(raw);

      if (normalized) {
        setData(normalized);
      } else {
        console.warn('No API data available, using mock data fallback');
        setData(mockData);
      }
    } catch (err: any) {
      console.error('BTC Hedge: API error:', err);
      console.warn('API failed, using mock data fallback');
      setData(mockData);
      setError(null); // Clear error since we're showing mock data
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if opened AND (no data OR timeframe changed)
    if (isOpen && !data) {
      fetchHedgePerformers(activeTimeframe);
    }
  }, [isOpen]);

  // Fetch when timeframe changes (regardless of isOpen state)
  useEffect(() => {
    if (data) {
      fetchHedgePerformers(activeTimeframe);
    }
  }, [activeTimeframe]);

  // Add horizontal scroll on wheel
  useEffect(() => {
    const scrollContainer = tableScrollRef.current;
    if (!scrollContainer) return;

    const handleWheel = (e: WheelEvent) => {
      // Check if table has horizontal overflow
      const hasHorizontalScroll = scrollContainer.scrollWidth > scrollContainer.clientWidth;

      if (hasHorizontalScroll) {
        e.preventDefault();
        e.stopPropagation();

        // Convert vertical scroll to horizontal scroll
        const scrollAmount = e.deltaY;
        scrollContainer.scrollLeft += scrollAmount;
      }
    };

    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
    };
  }, [data]); // Re-run when data changes

  const toggleColumn = (columnKey: string) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey]
    };
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('hedgePerformersBtcColumns', JSON.stringify(newVisibleColumns));
  };

  const togglePin = (columnKey: string) => {
    const isPinned = pinnedColumns.includes(columnKey);

    if (isPinned) {
      // Unpin column
      const filteredColumns = pinnedColumns.filter(col => col !== columnKey);
      const newPinnedColumns = sortPinnedColumns(filteredColumns);
      setPinnedColumns(newPinnedColumns);
      localStorage.setItem('hedgePerformersBtcPinnedColumns', JSON.stringify(newPinnedColumns));
    } else {
      // Check limit
      if (pinnedColumns.length >= 6) {
        setShowPinLimitToast(true);
        setTimeout(() => setShowPinLimitToast(false), 3000);
        return;
      }

      // Pin column and sort according to PIN_ORDER
      const unsortedColumns = [...pinnedColumns, columnKey];
      const newPinnedColumns = sortPinnedColumns(unsortedColumns);
      setPinnedColumns(newPinnedColumns);
      localStorage.setItem('hedgePerformersBtcPinnedColumns', JSON.stringify(newPinnedColumns));
    }
  };

  // Define natural column order for consistent pinning
  const PIN_ORDER = [
    'avgReturn',
    'hitRate',
    'correlation',
    'volatility',
    'hedgeScore',
  ];

  // Helper function to sort pinned columns according to PIN_ORDER
  const sortPinnedColumns = (columns: string[]): string[] => {
    return PIN_ORDER.filter(col => columns.includes(col));
  };

  const getColumnLeftPosition = (columnKey: string): number => {
    const columnWidths: {[key: string]: number} = {
      rank: 80,
      coin: 180,
      avgReturn: 130,
      hitRate: 120,
      correlation: 120,
      volatility: 110,
      hedgeScore: 120,
    };

    // Ensure we work with the properly ordered pinned columns
    const orderedPinnedColumns = sortPinnedColumns(pinnedColumns);
    const columnIndex = orderedPinnedColumns.indexOf(columnKey);
    if (columnIndex === -1) return 0; // Not pinned

    // Start after the permanently sticky columns (rank + coin)
    let leftPosition = (columnWidths.rank || 0) + (columnWidths.coin || 0);

    // Add widths of pinned columns that come before this one in PIN_ORDER
    for (let i = 0; i < columnIndex; i++) {
      const prevColumn = orderedPinnedColumns[i];
      leftPosition += columnWidths[prevColumn] || 100;
    }
    return leftPosition;
  };

  const formatPercentage = (value: number) => {
    const color = value >= 0
      ? (isDark ? 'text-emerald-400' : 'text-green-600')
      : (isDark ? 'text-red-400' : 'text-red-600');
    const sign = value >= 0 ? '+' : '';
    return (
      <span className={color}>{sign}{value.toFixed(2)}%</span>
    );
  };

  const formatCorrelation = (value: number) => {
    const absValue = Math.abs(value);
    let color = isDark ? 'text-gray-400' : 'text-gray-600';

    if (absValue < 0.2) color = isDark ? 'text-emerald-400' : 'text-green-600'; // Good hedge
    else if (absValue < 0.5) color = isDark ? 'text-yellow-400' : 'text-yellow-600'; // Moderate
    else color = isDark ? 'text-red-400' : 'text-red-600'; // High correlation

    return (
      <span className={color}>{value.toFixed(3)}</span>
    );
  };

  const getHedgeScoreBadge = (score: number) => {
    if (isDark) {
      if (score >= 70) return 'bg-emerald-400/20 text-emerald-400 border-emerald-400/30';
      if (score >= 50) return 'bg-yellow-400/20 text-yellow-400 border-yellow-400/30';
      return 'bg-red-400/20 text-red-400 border-red-400/30';
    } else {
      if (score >= 70) return 'bg-green-100 text-green-800 border-green-200';
      if (score >= 50) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      return 'bg-red-100 text-red-800 border-red-200';
    }
  };

  const getResilienceBadge = (hitRate: number) => {
    if (hitRate >= 60) {
      return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
          isDark
            ? 'bg-emerald-400/20 text-emerald-400 border-emerald-400/30'
            : 'bg-green-100 text-green-800 border-green-200'
        }`}>
          🛡️
        </span>
      );
    }
    return null;
  };

  const timeframeLabels = {
    '1Y': '1 Year',
    '1M': '1 Month'
  };

  return (
    <div className={`rounded-2xl shadow-sm border mb-6 transition-all duration-200 ${
      isDark
        ? 'bg-white/[0.03] border-white/10'
        : 'bg-white border-gray-200'
    }`}>
      {/* Header */}
      <div
        className={`px-6 py-4 border-b cursor-pointer transition-colors duration-200 ${
          isDark
            ? 'border-white/10 hover:bg-white/5'
            : 'border-gray-200 hover:bg-gray-50'
        }`}
        onClick={handleToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
              <Shield className={`${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} size={24} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Hedge Performers — When BTC Falls
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Top coins that performed well when Bitcoin declined over different timeframes
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="group relative">
              <span className={`text-lg cursor-help ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>ℹ️</span>
              <div className={`absolute bottom-full right-0 mb-2 w-80 p-3 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 ${
                isDark ? 'bg-gray-800 text-white' : 'bg-gray-900 text-white'
              }`}>
                <div className="font-medium mb-1">How Hedge Score is Calculated:</div>
                <div className="text-xs space-y-1">
                  <div>• <strong>Avg Return:</strong> Average % return during BTC down days (40% weight)</div>
                  <div>• <strong>Hit Rate:</strong> % of BTC down days coin was positive (30% weight)</div>
                  <div>• <strong>Correlation:</strong> Lower correlation with BTC is better (20% weight)</div>
                  <div>• <strong>Volatility:</strong> Lower volatility is better (10% weight)</div>
                </div>
              </div>
            </div>
            <svg
              className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''} ${
                isDark ? 'text-gray-400' : 'text-gray-400'
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      <div className={`transition-all duration-300 ease-in-out ${
        isOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
      }`}>
        {isOpen && (
          <div>
            {/* Timeframe Tabs */}
            <div className="px-6 pt-6">
              <div className={`flex space-x-1 mb-6 p-1 rounded-lg w-fit ${
                isDark ? 'bg-white/[0.05]' : 'bg-gray-100'
              }`}>
              {Object.entries(timeframeLabels).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setActiveTimeframe(key as '1Y' | '1M')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                    activeTimeframe === key
                      ? isDark
                        ? 'bg-emerald-400/20 text-emerald-400 shadow-sm'
                        : 'bg-white text-blue-600 shadow-sm'
                      : isDark
                        ? 'text-gray-400 hover:text-white'
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {label}
                </button>
              ))}
              </div>
            </div>

            {/* Summary Insight */}
            {data?.summary && (
              <div className="px-6">
                <div className={`border rounded-lg p-4 mb-6 ${
                isDark
                  ? 'bg-blue-500/10 border-blue-400/20'
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-start space-x-3">
                  <span className={`text-lg mt-0.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>ℹ️</span>
                  <div>
                    <p className={`font-medium ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>{data.summary.insight}</p>
                    <div className={`mt-2 flex flex-wrap gap-4 text-sm ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                      <span>Total Analyzed: <strong>{data.summary.total_analyzed}</strong></span>
                      <span>Positive Performers: <strong>{data.summary.positive_performers}</strong></span>
                      <span>Avg Hedge Score: <strong>{data.summary.avg_hedge_score}</strong></span>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="px-6">
                <div className="flex items-center justify-center py-12">
                <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isDark ? 'border-emerald-400' : 'border-blue-600'}`}></div>
                <span className={`ml-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading hedge performers...</span>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="px-6">
                <div className={`border rounded-lg p-4 ${
                isDark
                  ? 'bg-red-500/10 border-red-400/20'
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center space-x-2">
                  <span className={isDark ? 'text-red-400' : 'text-red-600'}>❌</span>
                  <span className={`font-medium ${isDark ? 'text-red-400' : 'text-red-800'}`}>Error loading data:</span>
                  <span className={isDark ? 'text-red-300' : 'text-red-700'}>{error}</span>
                </div>
                </div>
              </div>
            )}

            {/* Data Table */}
            {data?.performers && data.performers.length > 0 && (
              <div
                ref={tableScrollRef}
                className={`overflow-x-auto custom-scrollbar ${!isDark ? 'light-scrollbar' : ''} relative`}
                title="Use mouse wheel to scroll horizontally"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: isDark ? '#4b5563 #1f2937' : '#d1d5db #f3f4f6'
                }}
              >
                <table className={`w-full text-sm ${isDark ? '' : ''}`}>
                  <thead className={`text-xs uppercase tracking-wide ${isDark ? 'opacity-80' : 'opacity-80'}`}>
                    <tr className={`h-24 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                      <th
                        className={`h-24 text-center align-top sticky left-0 z-10 bg-inherit`}
                        style={{ width: 'max-content', minWidth: 'max-content', position: 'sticky', left: 0, top: 0, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' }}
                      >
                        <CoinHeaderCell title="Rank" />
                      </th>
                      <th
                        className={`h-24 text-center align-top sticky z-10 bg-inherit`}
                        style={{ width: '180px', minWidth: '180px', position: 'sticky', left: '80px', top: 0, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' }}
                      >
                        <CoinHeaderCell title="Coin" />
                      </th>
                      <th
                        className={getTableHeaderCellClasses(visibleColumns.avgReturn, pinnedColumns.includes('avgReturn'), isDark)}
                        style={getStickyPositionStyle(pinnedColumns.includes('avgReturn'), getColumnLeftPosition('avgReturn'), isDark)}
                      >
                        <StandardHeaderCell
                          colKey="avgReturn"
                          label="Avg Return (BTC Down)"
                          abbrev="AR"
                          visible={visibleColumns.avgReturn}
                          pinned={pinnedColumns.includes('avgReturn')}
                          onToggleColumn={toggleColumn}
                          onTogglePin={togglePin}
                        />
                      </th>
                      <th
                        className={getTableHeaderCellClasses(visibleColumns.hitRate, pinnedColumns.includes('hitRate'), isDark)}
                        style={getStickyPositionStyle(pinnedColumns.includes('hitRate'), getColumnLeftPosition('hitRate'), isDark)}
                      >
                        <StandardHeaderCell
                          colKey="hitRate"
                          label="Hit Rate"
                          abbrev="HR"
                          visible={visibleColumns.hitRate}
                          pinned={pinnedColumns.includes('hitRate')}
                          onToggleColumn={toggleColumn}
                          onTogglePin={togglePin}
                        />
                      </th>
                      <th
                        className={getTableHeaderCellClasses(visibleColumns.correlation, pinnedColumns.includes('correlation'), isDark)}
                        style={getStickyPositionStyle(pinnedColumns.includes('correlation'), getColumnLeftPosition('correlation'), isDark)}
                      >
                        <StandardHeaderCell
                          colKey="correlation"
                          label="Correlation"
                          abbrev="C"
                          visible={visibleColumns.correlation}
                          pinned={pinnedColumns.includes('correlation')}
                          onToggleColumn={toggleColumn}
                          onTogglePin={togglePin}
                        />
                      </th>
                      <th
                        className={getTableHeaderCellClasses(visibleColumns.volatility, pinnedColumns.includes('volatility'), isDark)}
                        style={getStickyPositionStyle(pinnedColumns.includes('volatility'), getColumnLeftPosition('volatility'), isDark)}
                      >
                        <StandardHeaderCell
                          colKey="volatility"
                          label="Volatility"
                          abbrev="V"
                          visible={visibleColumns.volatility}
                          pinned={pinnedColumns.includes('volatility')}
                          onToggleColumn={toggleColumn}
                          onTogglePin={togglePin}
                        />
                      </th>
                      <th
                        className={getTableHeaderCellClasses(visibleColumns.hedgeScore, pinnedColumns.includes('hedgeScore'), isDark)}
                        style={getStickyPositionStyle(pinnedColumns.includes('hedgeScore'), getColumnLeftPosition('hedgeScore'), isDark)}
                      >
                        <StandardHeaderCell
                          colKey="hedgeScore"
                          label="Hedge Score"
                          abbrev="HS"
                          visible={visibleColumns.hedgeScore}
                          pinned={pinnedColumns.includes('hedgeScore')}
                          onToggleColumn={toggleColumn}
                          onTogglePin={togglePin}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.performers.map((performer, index) => {
                      return (
                      <tr key={`${performer.symbol}-${performer.rank}`} className={`${isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-gray-50'}`}>
                        {visibleColumns.rank && (
                          <td
                            className={`px-4 py-4 whitespace-nowrap text-sm font-medium sticky left-0 z-10 ${isDark ? 'text-white' : 'text-gray-900'}`}
                            style={{ width: 'max-content', minWidth: 'max-content', position: 'sticky', left: 0, top: 0, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' }}
                          >
                            #{performer.rank}
                          </td>
                        )}
                        {visibleColumns.coin && (
                          <td
                            className={`px-4 py-4 whitespace-nowrap sticky z-10 bg-inherit`}
                            style={{ width: '180px', minWidth: '180px', position: 'sticky', left: '80px', top: 0, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' }}
                          >
                            <div className="flex items-center">
                              <CoinLogoImage
                                src={performer.logo_url || ''}
                                alt={performer.symbol}
                                size="md"
                                className="mr-3"
                              />
                              <div className="flex items-center gap-2">
                                <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{performer.symbol}</div>
                                {getResilienceBadge(performer.hit_rate)}
                              </div>
                            </div>
                          </td>
                        )}
                        <td
                          className={`py-2 px-4 text-center align-middle transition-all duration-300 ${
                            visibleColumns.avgReturn ? '' : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('avgReturn') ? 'sticky' : ''}`}
                          style={pinnedColumns.includes('avgReturn')
                            ? { position: 'sticky', left: `${getColumnLeftPosition('avgReturn')}px`, top: 0, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' }
                            : undefined}
                        >
                          <ColumnTooltip
                            content={
                              !visibleColumns.avgReturn ? (
                                <div className="flex items-center justify-center space-x-2">
                                  <span className={performer.avg_return_btc_down >= 0 ? (isDark ? 'text-emerald-400' : 'text-green-600') : (isDark ? 'text-red-400' : 'text-red-600')}>
                                    {performer.avg_return_btc_down >= 0 ? '↗️' : '↘️'}
                                  </span>
                                  {formatPercentage(performer.avg_return_btc_down)}
                                </div>
                              ) : null
                            }
                            onColumnClick={() => toggleColumn('avgReturn')}
                          >
                            {visibleColumns.avgReturn ? (
                              <div className="flex items-center justify-center space-x-2">
                                <span className={performer.avg_return_btc_down >= 0 ? (isDark ? 'text-emerald-400' : 'text-green-600') : (isDark ? 'text-red-400' : 'text-red-600')}>
                                  {performer.avg_return_btc_down >= 0 ? '↗️' : '↘️'}
                                </span>
                                {formatPercentage(performer.avg_return_btc_down)}
                              </div>
                            ) : (
                              <div className="text-xs opacity-50">•</div>
                            )}
                          </ColumnTooltip>
                        </td>
                        <td
                          className={`py-2 text-center transition-all duration-300 ${
                            visibleColumns.hitRate
                              ? 'px-4'
                              : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('hitRate') ? 'sticky' : ''} ${isDark ? 'text-white' : 'text-gray-900'}`}
                          style={pinnedColumns.includes('hitRate')
                            ? { position: 'sticky', left: `${getColumnLeftPosition('hitRate')}px`, top: 0, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' }
                            : undefined}
                        >
                          {visibleColumns.hitRate ? (
                            <div className="flex items-center justify-center space-x-2">
                              <span>{performer.hit_rate.toFixed(1)}%</span>
                              <div className={`w-16 rounded-full h-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                <div
                                  className={`h-2 rounded-full ${isDark ? 'bg-blue-400' : 'bg-blue-600'}`}
                                  style={{ width: `${Math.min(100, performer.hit_rate)}%` }}
                                ></div>
                              </div>
                            </div>
                          ) : (
                            <ColumnTooltip
                              content={<div className="flex items-center justify-center space-x-2">
                                <span>{performer.hit_rate.toFixed(1)}%</span>
                                <div className={`w-16 rounded-full h-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                  <div
                                    className={`h-2 rounded-full ${isDark ? 'bg-blue-400' : 'bg-blue-600'}`}
                                    style={{ width: `${Math.min(100, performer.hit_rate)}%` }}
                                  ></div>
                                </div>
                              </div>}
                              onColumnClick={() => toggleColumn('hitRate')}
                            >
                              <div className="py-1.5">
                                <div className="text-xs opacity-50">•</div>
                              </div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`py-2 text-center transition-all duration-300 ${
                            visibleColumns.correlation
                              ? 'px-4'
                              : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('correlation') ? 'sticky' : ''}`}
                          style={pinnedColumns.includes('correlation')
                            ? { position: 'sticky', left: `${getColumnLeftPosition('correlation')}px`, top: 0, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' }
                            : undefined}
                        >
                          {visibleColumns.correlation ? (
                            formatCorrelation(performer.correlation_with_btc)
                          ) : (
                            <ColumnTooltip
                              content={formatCorrelation(performer.correlation_with_btc)}
                              onColumnClick={() => toggleColumn('correlation')}
                            >
                              <div className="py-1.5">
                                <div className="text-xs opacity-50">•</div>
                              </div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`py-2 text-center transition-all duration-300 ${
                            visibleColumns.volatility
                              ? 'px-4'
                              : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('volatility') ? 'sticky' : ''} ${isDark ? 'text-white' : 'text-gray-900'}`}
                          style={pinnedColumns.includes('volatility')
                            ? { position: 'sticky', left: `${getColumnLeftPosition('volatility')}px`, top: 0, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' }
                            : undefined}
                        >
                          {visibleColumns.volatility ? (
                            <span>{performer.volatility.toFixed(2)}%</span>
                          ) : (
                            <ColumnTooltip
                              content={<span>{performer.volatility.toFixed(2)}%</span>}
                              onColumnClick={() => toggleColumn('volatility')}
                            >
                              <div className="py-1.5">
                                <div className="text-xs opacity-50">•</div>
                              </div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`py-2 text-center transition-all duration-300 ${
                            visibleColumns.hedgeScore
                              ? 'px-4'
                              : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('hedgeScore') ? 'sticky' : ''}`}
                          style={pinnedColumns.includes('hedgeScore')
                            ? { position: 'sticky', left: `${getColumnLeftPosition('hedgeScore')}px`, top: 0, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' }
                            : undefined}
                        >
                          {visibleColumns.hedgeScore ? (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getHedgeScoreBadge(performer.hedge_score)}`}>
                              {performer.hedge_score.toFixed(0)}
                            </span>
                          ) : (
                            <ColumnTooltip
                              content={<span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getHedgeScoreBadge(performer.hedge_score)}`}>
                                {performer.hedge_score.toFixed(0)}
                              </span>}
                              onColumnClick={() => toggleColumn('hedgeScore')}
                            >
                              <div className="py-1.5">
                                <div className="text-xs opacity-50">•</div>
                              </div>
                            </ColumnTooltip>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* No Data State */}
            {data?.performers && data.performers.length === 0 && !loading && (
              <div className="text-center py-12">
                <div className="text-5xl text-gray-400 mb-4">🛡️</div>
                <h3 className={`mt-2 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>No hedge performers found</h3>
                <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  No data available for the selected timeframe.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Toast notification for pin limit */}
      {showPinLimitToast && (
        <div
          className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-50 ${
            isDark
              ? 'bg-red-500/90 text-white border border-red-400'
              : 'bg-red-100 text-red-900 border border-red-300'
          }`}
          role="alert"
        >
          Limit reached (6). Unpin another column to continue.
        </div>
      )}
    </div>
  );
};

export default HedgePerformers;
